import React from "react";
import { VisibilityConfigNode } from "../../../../usecases/HomeConfig/types";
import {
  TagKeysAndValues,
  FreeformValueKeys,
} from "../../../../usecases/HomeConfig/tagKeys";
import { KeyValueInput, KeyValuePair } from "../../../KeyValueInput";

type RefField = "tags" | "filterByOption" | "tagsOption";

export interface VisibilityNodeEditorProps {
  node: VisibilityConfigNode;
  refSources: Record<string, string[]>;
  onChange: (
    updater: (node: VisibilityConfigNode) => VisibilityConfigNode,
  ) => void;
}

export const VisibilityNodeEditor = ({
  node,
  refSources,
  onChange,
}: VisibilityNodeEditorProps) => {
  const pairsOf = (field: RefField): KeyValuePair[] => {
    return Object.entries(node[field] ?? {}).map(([key, value]) => {
      return { key, value };
    });
  };

  const setEntry = (field: RefField, pair: KeyValuePair) => {
    onChange((current) => {
      return {
        ...current,
        [field]: { ...current[field], [pair.key]: pair.value },
      };
    });
  };

  const removeEntry = (field: RefField, index: number) => {
    onChange((current) => {
      const next = { ...current[field] };
      delete next[Object.keys(next)[index]];
      return { ...current, [field]: next };
    });
  };

  const handleAddTag = (pair: KeyValuePair) => {
    setEntry("tags", pair);
  };

  const handleRemoveTag = (index: number) => {
    removeEntry("tags", index);
  };

  const handleAddFilterByOption = (pair: KeyValuePair) => {
    setEntry("filterByOption", pair);
  };

  const handleRemoveFilterByOption = (index: number) => {
    removeEntry("filterByOption", index);
  };

  const handleAddTagsOption = (pair: KeyValuePair) => {
    setEntry("tagsOption", pair);
  };

  const handleRemoveTagsOption = (index: number) => {
    removeEntry("tagsOption", index);
  };

  return (
    <>
      <KeyValueInput
        label="Tags"
        sourceKeyValues={TagKeysAndValues}
        pairs={pairsOf("tags")}
        freeformKeys={FreeformValueKeys}
        onAdd={handleAddTag}
        onRemove={handleRemoveTag}
      />
      {/* NOTE: the value column names a group, not a tag value — the buyer binds
          that group's current pick before the send, so one node can filter on an
          axis another option owns. `<Any>` is meaningless for a reference. */}
      <KeyValueInput
        label="Filter By Option"
        sourceKeyValues={refSources}
        pairs={pairsOf("filterByOption")}
        hasAnyValue={false}
        onAdd={handleAddFilterByOption}
        onRemove={handleRemoveFilterByOption}
      />
      <KeyValueInput
        label="Tags Option"
        sourceKeyValues={refSources}
        pairs={pairsOf("tagsOption")}
        hasAnyValue={false}
        onAdd={handleAddTagsOption}
        onRemove={handleRemoveTagsOption}
      />
    </>
  );
};
